import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Unplug } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import {
  fetchConnections,
  fetchMcpClientPolicy,
  MCP_CLIENT_VENDOR_OPTIONS,
  revokeConnection,
  scopeLabel,
  updateMcpClientPolicy,
  type McpClientVendor,
} from '@/features/settings/mcp-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

export function SettingsConnectedAppsPage() {
  const queryClient = useQueryClient()
  const { workspace } = useWorkspace()

  const connectionsQuery = useQuery({
    queryKey: ['mcp-connections', workspace.id],
    queryFn: () => fetchConnections(),
    retry: false,
  })

  const revoke = useMutation({
    mutationFn: (id: string) => revokeConnection(id),
    onSuccess: () => {
      toast.success('Verbindung widerrufen.')
      void queryClient.invalidateQueries({ queryKey: ['mcp-connections', workspace.id] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Widerrufen fehlgeschlagen'),
  })

  function handleRevoke(id: string) {
    if (window.confirm('Verbindung widerrufen? Die App verliert sofort den Zugriff.')) {
      revoke.mutate(id)
    }
  }

  const notFound =
    connectionsQuery.error instanceof ApiError &&
    connectionsQuery.error.status === 404 &&
    connectionsQuery.error.code === 'not_found'

  const connections = connectionsQuery.data?.connections ?? []

  return (
    <div className="mx-auto w-full max-w-[1180px] space-y-6 px-4 py-6 min-[761px]:px-8 min-[761px]:py-8">
      <header>
        <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">Verbundene Apps</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          KI-Apps wie Claude, ChatGPT oder Mistral, die über MCP auf diesen Workspace zugreifen.
        </p>
      </header>

      {connectionsQuery.isLoading ? (
        <Card>
          <CardContent className="space-y-3 pt-6">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      ) : notFound ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              Verbindungen zu externen Apps sind auf diesem Server nicht aktiviert.
            </p>
          </CardContent>
        </Card>
      ) : connectionsQuery.error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              {connectionsQuery.error instanceof Error
                ? connectionsQuery.error.message
                : 'Verbindungen konnten nicht geladen werden.'}
            </p>
          </CardContent>
        </Card>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="space-y-1 pt-6">
            <p className="text-sm font-medium">Noch keine App verbunden.</p>
            <p className="text-sm text-muted-foreground">
              In Claude: Einstellungen › Connectors › Custom Connector mit der MCP-Adresse dieses Servers hinzufügen.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-[16px] font-[650]">Verbindungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {connections.map((connection) => (
              <div
                key={connection.id}
                className="grid gap-3 rounded-md border p-3 min-[761px]:grid-cols-[1fr_auto] min-[761px]:items-center"
              >
                <div className="min-w-0 space-y-1">
                  <p className="break-words text-sm font-medium">{connection.client_name}</p>
                  <p className="break-all font-mono text-xs text-muted-foreground">{connection.client_id}</p>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {connection.scopes.map((scope) => (
                      <Badge key={scope} variant="secondary">
                        {scopeLabel(scope)}
                      </Badge>
                    ))}
                  </div>
                  <p className="pt-1 text-xs text-muted-foreground">
                    Verbunden am {new Date(connection.created_at).toLocaleString('de-CH')} ·{' '}
                    {connection.last_used_at
                      ? `Zuletzt genutzt ${new Date(connection.last_used_at).toLocaleString('de-CH')}`
                      : 'Noch nicht genutzt'}{' '}
                    · {connection.is_own ? 'Von dir' : 'Anderes Mitglied'}
                  </p>
                </div>
                {connection.can_revoke ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-destructive"
                    disabled={revoke.isPending}
                    onClick={() => handleRevoke(connection.id)}
                  >
                    <Unplug className="h-4 w-4" />
                    Widerrufen
                  </Button>
                ) : null}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <McpClientPolicyCard />

      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Was eine verbundene App liest, verlässt MindKeep. Besitzer und Admins können alle Verbindungen des
            Workspace widerrufen, andere Mitglieder nur ihre eigenen.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

function McpClientPolicyCard() {
  const queryClient = useQueryClient()
  const { workspace } = useWorkspace()

  const policyQuery = useQuery({
    queryKey: ['mcp-client-policy', workspace.id],
    queryFn: () => fetchMcpClientPolicy(),
    retry: false,
  })

  const policy = policyQuery.data
  const [draft, setDraft] = useState<McpClientVendor[]>([])

  useEffect(() => {
    if (policy) setDraft(policy.allowed_clients)
  }, [policy])

  const save = useMutation({
    mutationFn: (allowed: McpClientVendor[]) => updateMcpClientPolicy(allowed),
    onSuccess: (data) => {
      toast.success('Anbieter gespeichert.')
      queryClient.setQueryData(['mcp-client-policy', workspace.id], data)
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Speichern fehlgeschlagen'),
  })

  const notFound =
    policyQuery.error instanceof ApiError &&
    policyQuery.error.status === 404 &&
    policyQuery.error.code === 'not_found'

  if (notFound) return null

  if (policyQuery.isLoading) {
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    )
  }

  if (policyQuery.error || !policy) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            {policyQuery.error instanceof Error ? policyQuery.error.message : 'Anbieter konnten nicht geladen werden.'}
          </p>
        </CardContent>
      </Card>
    )
  }

  const serverList = policy.allowed_clients
  const draftSet = new Set(draft)
  const serverSet = new Set(serverList)
  const isDirty =
    draft.length !== serverList.length || draft.some((vendor) => !serverSet.has(vendor))
  const hasOutsideEu = draft.some((vendor) => {
    const option = MCP_CLIENT_VENDOR_OPTIONS.find((o) => o.vendor === vendor)
    return option?.outsideEu === true
  })

  const euOnly = policy.eu_only

  function toggleVendor(vendor: McpClientVendor, outsideEu: boolean) {
    if (euOnly && outsideEu && !draftSet.has(vendor)) {
      const confirmed = window.confirm(
        'Inhalte dieses Workspace gehen dann an einen Anbieter ausserhalb der EU. Trotzdem erlauben?',
      )
      if (!confirmed) return
    }
    setDraft((prev) => (prev.includes(vendor) ? prev.filter((v) => v !== vendor) : [...prev, vendor]))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[16px] font-[650]">Erlaubte Anbieter</CardTitle>
        <CardDescription>
          Welche KI-Apps sich mit diesem Workspace verbinden dürfen. Gilt für neue Verbindungen und für jeden Zugriff
          bestehender Verbindungen.
        </CardDescription>
        {!policy.explicit ? (
          <p className="text-xs text-muted-foreground">
            Standard aktiv:{' '}
            {policy.eu_only
              ? 'nur Mistral (EU), weil der Workspace auf EU beschränkt ist.'
              : 'alle Anbieter erlaubt.'}
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-2">
        {MCP_CLIENT_VENDOR_OPTIONS.map((option) => (
          <div key={option.vendor} className="space-y-1">
            <label
              className="flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm has-[:checked]:border-primary"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={draftSet.has(option.vendor)}
                disabled={!policy.can_edit}
                onChange={() => toggleVendor(option.vendor, option.outsideEu)}
              />
              <span className="font-medium">{option.label}</span>
            </label>
            {option.vendor === 'local' ? (
              <p className="pl-1 text-xs text-muted-foreground">
                Zum Beispiel Claude Code oder Vibe CLI auf dem eigenen Rechner. Der Anbieter dahinter lässt sich nicht
                prüfen.
              </p>
            ) : null}
          </div>
        ))}

        {policy.eu_only && hasOutsideEu ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
            Inhalte dieses Workspace gehen dann an einen Anbieter ausserhalb der EU.
          </div>
        ) : null}

        {draft.length === 0 ? (
          <p className="text-xs text-muted-foreground">Keine App kann sich verbinden.</p>
        ) : null}

        {!policy.can_edit ? (
          <p className="text-xs text-muted-foreground">Nur Besitzer und Admins können das ändern.</p>
        ) : (
          <div>
            <Button
              type="button"
              size="sm"
              disabled={!isDirty || save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {save.isPending ? 'Wird gespeichert…' : 'Speichern'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
